import React, { useCallback, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './HoverTooltip.scss'

/**
 * HoverTooltip — lightweight hint shown on hover/focus.
 *
 * Rendered via a portal to <body> so it escapes any `overflow: hidden`
 * or `contain: paint` ancestor. Visuals follow the design system:
 * `--color-surface-1` background + `--shadow-level-2` shadow.
 */
const HoverTooltip = ({
  content,
  children,
  placement = 'top', // 'top' | 'bottom'
  offset = 8, // px between trigger and tooltip
  className = '',
}) => {
  const triggerRef = useRef(null)
  const [show, setShow] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return
    const r = triggerRef.current.getBoundingClientRect()
    if (placement === 'bottom') {
      setPos({ top: r.bottom + offset, left: r.left + r.width / 2 })
    } else {
      setPos({ top: r.top - offset, left: r.left + r.width / 2 })
    }
  }, [placement, offset])

  const handleEnter = () => {
    updatePosition()
    setShow(true)
  }
  const handleLeave = () => setShow(false)

  const transform =
    placement === 'bottom' ? 'translate(-50%, 0)' : 'translate(-50%, -100%)'

  return (
    <>
      <div
        ref={triggerRef}
        className="hover-tooltip__trigger"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={handleEnter}
        onBlur={handleLeave}>
        {children}
      </div>
      {show &&
        createPortal(
          <div
            className={`hover-tooltip ${className}`}
            style={{ top: pos.top, left: pos.left, transform }}>
            {content}
          </div>,
          document.body
        )}
    </>
  )
}

export default HoverTooltip
